import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { IconsDirective } from '@pkg-directives/icons/icons.directive';
import { CookiesService } from '@pkg-services/cookies/cookies.service';
import { AdminService } from '@pkg-services/admin/admin.service';
import { ApiCallService } from '@pkg-services/callApi/api-call.service';
import { AdminActionCenterService } from '@pkg-services/admin/admin-action-center.service';
import type { AdminRight } from '@pkg-framework/models/admin';
import { RouteAdminLogout } from '@pkg-framework/models/admin';
import { SidebarBrandComponent } from './sidebar-brand/sidebar-brand.component';
import { SidebarMenuGroupComponent } from './sidebar-menu-group/sidebar-menu-group.component';
import { SidebarFooterComponent } from './sidebar-footer/sidebar-footer.component';

export interface SidebarMenuGroup {
  /** Bloc du menu (« Gestion », « Plateforme ») : un intertitre l'annonce. */
  section?: string;
  label: string;
  icon?: string;
  items: SidebarMenuItem[];
}

export interface SidebarMenuItem {
  label: string;
  route: string;
  icon: string;
  /** Droit requis (DROITS.md). Si absent, l’item est visible par tous les admins connectés. */
  right?: AdminRight;
  /** Clé pour badge action center (ex. onboarding_pending). */
  actionBadgeKey?: string;
  /** Plusieurs compteurs additionnés : entrée d'une rubrique à onglets. */
  actionBadgeKeys?: string[];
  queryParams?: Record<string, string>;
  /** Actif seulement sur sa propre adresse (une page d'accueil de section, pas ses sous-pages). */
  exact?: boolean;
  /** Adresses où l'entrée est active, avec leurs sous-pages (par défaut : sa route). */
  activeUnder?: string[];
  /** Sous-pages qui relèvent d'une autre entrée. */
  notUnder?: string[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    IconsDirective,
    SidebarBrandComponent,
    SidebarMenuGroupComponent,
    SidebarFooterComponent
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent {
  @Input() isOpen = true;
  @Input() isMobileOpen = false;
  @Output() closeMobile = new EventEmitter<void>();

  readonly sidebarWidth = 72;

  hoverTooltipText = '';
  hoverTooltipVisible = false;
  hoverTooltipY = 0;

  actionBadges: Record<string, number> = {};

  constructor(
    private cookies: CookiesService,
    private adminService: AdminService,
    private actionCenter: AdminActionCenterService,
    private api: ApiCallService,
    private router: Router
  ) {
    this.actionCenter.watch().subscribe((data) => {
      const map: Record<string, number> = { ...(data?.sidebar_counts ?? {}) };
      const sidebarBadgeIds = [
        'onboarding_pending',
        'activity_add_requests',
        'contact_messages',
        'listing_reports',
        'prospection_follow_ups',
        'prospection_callbacks',
        'peekship_documents_pending',
        'city_hub_claims_pending',
      ] as const;
      for (const item of data?.items ?? []) {
        // Les compteurs du menu sont relus chaque minute : ils priment sur les alertes, plus anciennes.
        if (item.id && sidebarBadgeIds.includes(item.id as (typeof sidebarBadgeIds)[number]) && !(item.id in map)) {
          map[item.id] = item.count ?? 0;
        }
        if (item.id?.startsWith('audit_')) {
          map['payments_audit'] = (map['payments_audit'] ?? 0) + (item.count ?? 0);
        }
        if (item.id === 'stripe_inactive') {
          map['stripe_inactive'] = item.count ?? 0;
        }
      }
      this.actionBadges = map;
    });
    this.actionCenter.refresh().subscribe({ error: () => {} });
  }

  badgeFor(item: SidebarMenuItem): number {
    const keys = item.actionBadgeKeys ?? (item.actionBadgeKey ? [item.actionBadgeKey] : []);
    return keys.reduce((sum, key) => sum + (this.actionBadges[key] ?? 0), 0);
  }

  /**
   * Menu complet avec droits (doc super-admin DROITS.md). Trois blocs (30/10/2026) : « Gestion », le métier de tous
   * les jours ; « Commercial », démarchage et partenaires ; « Plateforme », le site et sa configuration.
   */
  private readonly menuGroupsRaw: SidebarMenuGroup[] = [
    {
      // Toutes les demandes entrantes à pastille, réunies (30/10/2026).
      section: 'Gestion',
      label: 'À traiter',
      icon: 'file-add',
      items: [
        {
          label: 'Demandes d’inscription',
          route: '/admin/onboarding/inscriptions',
          icon: 'file-add',
          right: 'organizations:onboarding',
          actionBadgeKey: 'onboarding_pending',
          queryParams: { status: 'pending' }
        },
        {
          label: 'Fiches signalées',
          route: '/admin/demandes/signalements',
          icon: 'warning',
          right: 'organizations:onboarding',
          actionBadgeKey: 'listing_reports',
          queryParams: { status: 'pending' }
        },
        {
          label: 'Messages contact',
          route: '/admin/demandes/ajout-activites',
          icon: 'sms',
          right: 'platform:activity_add_requests',
          actionBadgeKey: 'contact_messages',
          queryParams: { form_type: 'contact', status: 'pending' }
        },
        {
          // « Peek & Go prépare ma fiche » (03/11/2026) : la structure a donné son accord, la fiche attend d'être remplie.
          label: 'Fiches à préparer',
          route: '/admin/demandes/ajout-activites',
          icon: 'atelier',
          right: 'platform:activity_add_requests',
          actionBadgeKey: 'preparation_requests',
          queryParams: { form_type: 'preparation_request', status: 'pending' }
        },
        {
          label: 'Demandes d’activité',
          route: '/admin/demandes/ajout-activites',
          icon: 'documents',
          right: 'platform:activity_add_requests',
          actionBadgeKey: 'activity_add_requests',
          queryParams: { form_type: 'activity_add_request', status: 'pending' }
        }
      ]
    },
    {
      section: 'Gestion',
      label: 'Organisations',
      icon: 'organisation',
      items: [
        { label: 'Liste des organisations', route: '/admin/organisations', icon: 'organisation', right: 'organizations:view' },
      ]
    },
    {
      // Collectivités : une rubrique à part, comme les organisations (30/10/2026) — démarchage des mairies puis
      // gestion des communes inscrites. Leurs campagnes d'e-mails sont dans Démarchage › Campagnes.
      section: 'Gestion',
      label: 'Collectivités',
      icon: 'pin-full',
      items: [
        { label: 'Mairies', route: '/admin/collectivites/mairies', icon: 'list', right: 'prospection:manage' },
        { label: 'Import des mairies', route: '/admin/collectivites/import', icon: 'file-add', right: 'prospection:manage' },
        {
          label: 'Demandes de revendication',
          route: '/admin/collectivites/demandes',
          icon: 'file-add',
          right: 'cities:claims',
          actionBadgeKey: 'city_hub_claims_pending',
          queryParams: { status: 'pending' },
        },
        { label: 'Communes inscrites', route: '/admin/collectivites/communes', icon: 'list', right: 'cities:claims' },
        { label: 'Actualités communes', route: '/admin/collectivites/contenus', icon: 'megaphone', right: 'cities:content' },
      ]
    },
    {
      section: 'Gestion',
      label: 'Finance',
      icon: 'accounting',
      items: [
        { label: 'Finance plateforme', route: '/admin/paiements/finance', icon: 'accounting', right: 'payments:view' },
        { label: 'Transactions', route: '/admin/paiements/transactions', icon: 'transaction', right: 'payments:view' },
        // « Commandes » retiré du menu (doublon visuel de Transactions) : la route et le détail
        // commande restent actifs, ils sont ouverts depuis le hub organisation.
        {
          label: 'Santé paiements',
          route: '/admin/paiements/sante',
          icon: 'stats',
          right: 'payments:view',
          actionBadgeKey: 'payments_audit'
        }
      ]
    },
    {
      // Démarchage (30/10/2026) : une entrée par outil, ses pages en onglets dans la page (plus de doublons
      // menu / onglets). Pastille de l'entrée = somme de celles de ses onglets.
      section: 'Commercial',
      label: 'Démarchage',
      icon: 'megaphone',
      items: [
        {
          label: 'Prospection',
          route: '/admin/prospection',
          icon: 'megaphone',
          right: 'prospection:manage',
          actionBadgeKeys: ['prospection_follow_ups', 'prospection_callbacks', 'prospection_exclusions_to_review'],
          notUnder: ['/admin/prospection/campagnes'],
        },
        {
          label: 'Campagnes',
          route: '/admin/prospection/campagnes',
          icon: 'email-light',
          right: 'prospection:manage',
          activeUnder: ['/admin/prospection/campagnes', '/admin/collectivites/campagnes'],
        },
      ]
    },
    {
      section: 'Commercial',
      label: 'Peekship',
      icon: 'megaphone',
      items: [
        {
          label: 'Partenaires',
          route: '/admin/peekship/partenaires',
          icon: 'user',
          actionBadgeKey: 'peekship_documents_pending',
        },
        { label: 'Invitations', route: '/admin/peekship/invitations', icon: 'email-light' },
      ]
    },
    {
      section: 'Commercial',
      label: 'Régie publicitaire',
      icon: 'megaphone',
      items: [
        { label: 'Annonceurs', route: '/admin/advertising/advertisers', icon: 'megaphone', right: 'advertising:view' },
        { label: 'Emplacements', route: '/admin/advertising/placements', icon: 'pin-full', right: 'advertising:manage' }
      ]
    },
    {
      section: 'Plateforme',
      label: 'Contenu du site',
      icon: 'documents',
      items: [
        { label: 'Pages', route: '/admin/contenu/pages', icon: 'documents', right: 'content:pages' },
        { label: 'Médiathèque', route: '/admin/contenu/medias', icon: 'pictures', right: 'content:media' },
      ]
    },
    {
      section: 'Plateforme',
      label: 'Communication',
      icon: 'megaphone',
      items: [
        { label: 'Messages & annonces', route: '/admin/communications/messages', icon: 'sms', right: 'platform:communications' },
        { label: 'Statistiques', route: '/admin/communications/stats', icon: 'accounting', right: 'platform:communications' },
      ],
    },
    {
      section: 'Plateforme',
      label: 'Référentiels',
      icon: 'organisation',
      items: [
        { label: 'Thèmes', route: '/admin/referentiels/themes', icon: 'atelier', right: 'referentials:themes' },
        { label: 'Activités', route: '/admin/referentiels/activites', icon: 'adrenaline', right: 'referentials:activities' },
        { label: 'Catégories', route: '/admin/referentiels/categories', icon: 'puzzle', right: 'referentials:categories' },
        { label: 'Villes', route: '/admin/referentiels/villes', icon: 'pin-full', right: 'referentials:cities' },
      ]
    },
    {
      section: 'Plateforme',
      label: 'Paramètres',
      icon: 'align-justify',
      items: [
        { label: 'Informations légales', route: '/admin/informations-legales', icon: 'documents', right: 'platform:legal_identity' },
        { label: 'Conditions & CGU', route: '/admin/legal-documents', icon: 'sales-rules', right: 'platform:legal_documents' },
        { label: 'Équipe admin', route: '/admin/parametres/equipe', icon: 'user', right: 'platform:manage_admins' },
      ]
    },
    {
      section: 'Plateforme',
      label: 'Technique',
      icon: 'infos-bubble',
      items: [
        {
          // Visiteurs réels du site public, relevés dans les journaux du serveur.
          label: 'Fréquentation du site',
          route: '/admin/plateforme/frequentation',
          icon: 'stats',
          right: 'platform:dashboard'
        },
        {
          // Journal de l'API regroupé par type, paiements en tête (plutôt que des e-mails).
          label: 'Rapport d’erreurs',
          route: '/admin/plateforme/rapport-erreurs',
          icon: 'infos-bubble',
          right: 'platform:manage_admins'
        },
        { label: 'Mises à jour OTA', route: '/admin/plateforme/ota-mobile', icon: 'infos-bubble', right: 'platform:mobile_ota' },
        // Bloque l'accès à un espace (écran dédié jusqu'à clôture) : technique, pas de la communication (30/10/2026).
        { label: "Restrictions d'accès", route: '/admin/communications/restrictions', icon: 'calendar-close', right: 'platform:communications_restrictions' },
      ]
    }
  ];

  /** Groupes visibles selon droits (full_admin ou rights). */
  get menuGroups(): SidebarMenuGroup[] {
    return this.menuGroupsRaw
      .map(g => ({
        ...g,
        items: g.items
          .filter(item => !item.right || this.adminService.hasRight(item.right))
          .map(item => ({
            label: item.label,
            route: item.route,
            icon: item.icon,
            queryParams: item.queryParams,
            exact: item.exact,
            activeUnder: item.activeUnder,
            notUnder: item.notUnder,
            badge: this.badgeFor(item) || undefined
          }))
      }))
      .filter(g => g.items.length > 0);
  }

  /** Rubriques visibles rangées par bloc, dans l'ordre du menu. */
  get menuBlocks(): { section: string; groups: SidebarMenuGroup[] }[] {
    const blocks: { section: string; groups: SidebarMenuGroup[] }[] = [];
    for (const group of this.menuGroups) {
      const section = group.section ?? '';
      const last = blocks[blocks.length - 1];
      if (last && last.section === section) last.groups.push(group);
      else blocks.push({ section, groups: [group] });
    }
    return blocks;
  }

  /** Tableau de bord : un bouton seul au-dessus des blocs, pas une rubrique (30/10/2026). */
  get canSeeDashboard(): boolean {
    return this.adminService.hasRight('platform:dashboard');
  }

  /** Aucune rubrique ouverte d'office (30/10/2026) : seule celle de la page en cours se déplie. */
  isGroupOpenByDefault(_label: string): boolean {
    return false;
  }

  onItemHover(payload: { label: string; clientY: number }): void {
    this.hoverTooltipText = payload.label;
    this.hoverTooltipY = payload.clientY;
    this.hoverTooltipVisible = true;
  }

  onItemLeave(): void {
    this.hoverTooltipVisible = false;
  }

  onLogout(): void {
    this.api.post(RouteAdminLogout, {}).subscribe({
      next: () => this.doLogout(),
      error: () => this.doLogout()
    });
  }

  private doLogout(): void {
    this.adminService.clearCurrentAdmin();
    this.cookies.remove();
    this.router.navigate(['/login']);
  }
}
